import React, { useMemo } from 'react';
import { Box, Divider, InputAdornment, TextField, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { OCR_GUIDE_ROW_HEIGHT, buildOutlinedErrorBorderStyles, mergeOutlinedInputStyles, receiptOutlinedInputStyles } from './AuditDynamicFormConstants';
import { sanitizeVendingSalesDecimalInput, sanitizeVendingSalesIntegerInput } from '../../features/audit/fixOcr/utils/vendingSalesInputUtils';
import { formatCurrency, parseOptionalNumber } from '../../features/audit/fixOcr/utils/vendingTotalsUtils';

type VendingSalesFormTotalsProps = {
  control: any;
  errors?: any;
  gamesData: Map<string, number> | null;
};

const VendingSalesFormTotals: React.FC<VendingSalesFormTotalsProps> = React.memo(({ control, errors, gamesData }) => {
  const watchedLines = useWatch({
    control,
    name: 'parsed_data.final_json.lines',
  });

  const watchedSummary = useWatch({
    control,
    name: 'parsed_data.final_json.summary',
  });

  const {
    scratcherCount,
    scratcherAmount,
    drawGameCount,
    drawGameAmount,
    totalCount,
    totalAmount,
  } = useMemo(() => {
    const summary = watchedSummary as any;
    const drawCountFromSummary = parseOptionalNumber(summary?.draw_game_count) ?? 0;
    const drawAmountFromSummary = parseOptionalNumber(summary?.draw_game_amount) ?? 0;

    const lines = Array.isArray(watchedLines) ? watchedLines : [];
    const computed = lines.reduce((accumulator: { count: number; amount: number }, line: any) => {
      const gameId = line?.game != null ? String(line.game).trim() : '';
      const costPerTicket = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;
      const count = parseOptionalNumber(line?.count) ?? 0;
      return {
        count: accumulator.count + count,
        amount: accumulator.amount + costPerTicket * count,
      };
    }, { count: 0, amount: 0 });

    const total = computed.amount + drawAmountFromSummary;

    return {
      scratcherCount: computed.count,
      scratcherAmount: computed.amount,
      drawGameCount: drawCountFromSummary,
      drawGameAmount: drawAmountFromSummary,
      totalCount: computed.count + drawCountFromSummary,
      totalAmount: total,
    };
  }, [gamesData, watchedLines, watchedSummary]);

  const drawGameError = !!errors?.parsed_data?.final_json?.summary?.draw_game_amount;
  const drawGameCountError = !!errors?.parsed_data?.final_json?.summary?.draw_game_count;
  const inputSx = receiptOutlinedInputStyles;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: 0, py: 2 }}>
      <Box sx={{ width: '100%' }}>
        <Divider sx={{ borderColor: '#b98542', borderWidth: '1px', mb: 1.25 }} />

        {/* <Box
          display="flex"
          sx={{ py: 1, minHeight: '23px', maxHeight: '23px', alignItems: 'center' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: 14 }}>
              &nbsp;
            </Typography>
          </Box>
          <Box sx={{ width: 110, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: 14 }}>
              Count Total
            </Typography>
          </Box>
          <Box sx={{ width: 130, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: 14 }}>
              Amount Total
            </Typography>
          </Box>
        </Box> */}

        {/* <Divider sx={{ borderColor: '#A3AED099', borderWidth: '0.5px' }} /> */}

        <Box
          display="flex"
          sx={{ py: 0, height: OCR_GUIDE_ROW_HEIGHT, minHeight: OCR_GUIDE_ROW_HEIGHT, maxHeight: OCR_GUIDE_ROW_HEIGHT, alignItems: 'center', fontFamily: 'inherit' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 800, fontSize: 17 }}>
              SCRATCHERS
            </Typography>
          </Box>
          <Box sx={{ width: 112, textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: 17 }}>
              {Number.isFinite(scratcherCount) ? String(scratcherCount) : '0'}
            </Typography>
          </Box>
          <Box sx={{ width: 132, textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: 17 }}>
              {formatCurrency(scratcherAmount)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#d6b982', borderStyle: 'dashed', borderWidth: '0.5px' }} />

        <Box
          display="flex"
          sx={{ py: 0, height: OCR_GUIDE_ROW_HEIGHT, minHeight: OCR_GUIDE_ROW_HEIGHT, maxHeight: OCR_GUIDE_ROW_HEIGHT, alignItems: 'center', fontFamily: 'inherit' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 800, fontSize: 17 }}>
              DRAW GAMES
            </Typography>
          </Box>
          <Box sx={{ width: 112, display: 'flex', justifyContent: 'flex-end' }}>
            <Controller
              name="parsed_data.final_json.summary.draw_game_count"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(sanitizeVendingSalesIntegerInput(event.target.value).slice(0, 6))}
                  size="small"
                  placeholder={Number.isFinite(drawGameCount) ? String(drawGameCount) : '0'}
                  error={drawGameCountError}
                  sx={mergeOutlinedInputStyles({
                    ...inputSx,
                    ...buildOutlinedErrorBorderStyles(drawGameCountError),
                    width: 80,
                    minWidth: 80,
                    maxWidth: 80,
                    '& .MuiInputBase-input': {
                      ...inputSx['& .MuiInputBase-input'],
                      padding: '0px !important',
                      fontFamily: '"Courier Prime", "Courier New", monospace',
                      fontSize: 17,
                      fontWeight: 700,
                      textAlign: 'right',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '0px !important',
                    },
                  })}
                  InputLabelProps={{ shrink: false }}
                />
              )}
            />
          </Box>
          <Box sx={{ width: 132, display: 'flex', justifyContent: 'flex-end' }}>
            <Controller
              name="parsed_data.final_json.summary.draw_game_amount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(sanitizeVendingSalesDecimalInput(event.target.value).slice(0, 10))}
                  size="small"
                  placeholder={drawGameAmount === undefined ? '0.00' : String(drawGameAmount)}
                  error={drawGameError}
                  sx={mergeOutlinedInputStyles({
                    ...inputSx,
                    ...buildOutlinedErrorBorderStyles(drawGameError),
                    width: 120,
                    minWidth: 120,
                    maxWidth: 120,
                    '& .MuiOutlinedInput-root': {
                      paddingLeft: 0,
                    },
                    '& .MuiInputAdornment-root': {
                      marginRight: 0,
                    },
                    '& .MuiInputBase-input': {
                      ...inputSx['& .MuiInputBase-input'],
                      py: '0',
                      px: '6px',
                      fontFamily: '"Courier Prime", "Courier New", monospace',
                      fontSize: 17,
                      fontWeight: 700,
                      textAlign: 'right',
                    },
                  })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ m: 0, p: 0 }}>
                        <Typography component="span" sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: 17 }}>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: false }}
                />
              )}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#d6b982', borderStyle: 'dashed', borderWidth: '0.5px' }} />

        <Box
          display="flex"
          sx={{ py: 0, height: OCR_GUIDE_ROW_HEIGHT, minHeight: OCR_GUIDE_ROW_HEIGHT, maxHeight: OCR_GUIDE_ROW_HEIGHT, alignItems: 'center', fontFamily: 'inherit' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 900, fontSize: 17 }}>
              TOTAL
            </Typography>
          </Box>
          <Box sx={{ width: 112, textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 900, fontSize: 17 }}>
              {Number.isFinite(totalCount) ? String(totalCount) : '0'}
            </Typography>
          </Box>
          <Box sx={{ width: 132, textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontFamily: 'inherit', fontWeight: 900, fontSize: 17 }}>
              {formatCurrency(totalAmount)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#b98542', borderWidth: '1px' }} />
      </Box>
    </Box>
  );
});

VendingSalesFormTotals.displayName = 'VendingSalesFormTotals';

export default VendingSalesFormTotals;
